import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Eye, EyeOff, ArrowRight, ShieldAlert } from "lucide-react";
import { authService } from "@cabukaka/services";
import { useToken } from "@cabukaka/hooks";
import { useNavigate } from "react-router-dom";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { saveToken } = useToken()

  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Simulation de connexion (à remplacer par ton appel API / Firebase / Supabase)
    try {
      if (!email || !password) {
        throw new Error("Veuillez remplir tous les champs.");
      }

      // Simuler un délai réseau
      const response = await authService.login({ email, password });

      if (response.token) {
        saveToken(response.token)
        navigate(`/redirect?token=${response.token}`)
      }

      console.log("Connexion réussie", response);
    } catch (err: any) {
      console.log(err.rsponse)
      setError(err.message || "Identifiants incorrects.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen w-full bg-[#030303] flex items-center justify-center px-4 overflow-hidden">
      {/* Glows d'ambiance signature */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-zinc-900/20 blur-[100px] pointer-events-none" />

      {/* Cadre de connexion en verre sombre */}
      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md relative z-10 bg-[#070708]/80 border border-white/5 rounded-2xl p-8 sm:p-10 shadow-[0_30px_70px_rgba(0,0,0,0.8)] backdrop-blur-xl"
      >
        {/* Logo / Titre */}
        <div className="text-center mb-10">
          <h1 className="text-xl font-light tracking-[0.25em] text-white uppercase mb-2">
            Authentik <span className="font-serif italic font-normal text-transparent bg-clip-text bg-gradient-to-r from-[#FFE082] via-[#D4AF37] to-[#B38728]">Hair</span>
          </h1>
          <p className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">
            Espace Administration
          </p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Alerte Erreur */}
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-950/20 border border-red-500/20 text-red-400 text-xs font-light"
            >
              <ShieldAlert size={14} className="flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Input Email */}
          <div className="space-y-2">
            <label className="block text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
              Adresse Email
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                placeholder="admin@authentikhair.com"
                className="w-full bg-black/40 border border-white/5 text-white rounded-xl pl-11 pr-4 py-3.5 text-xs sm:text-sm focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/10 disabled:opacity-50 transition-all duration-300 placeholder:text-zinc-700 font-light"
              />
            </div>
          </div>

          {/* Input Mot de passe */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-[10px] font-mono tracking-widest text-zinc-400 uppercase">
                Mot de passe
              </label>
              <a
                href="#forgot"
                className="text-[9px] font-mono text-[#D4AF37] hover:text-[#FFE082] transition-colors uppercase tracking-wider"
              >
                Oublié ?
              </a>
            </div>
            <div className="relative">
              <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="••••••••••••"
                className="w-full bg-black/40 border border-white/5 text-white rounded-xl pl-11 pr-11 py-3.5 text-xs sm:text-sm focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/10 disabled:opacity-50 transition-all duration-300 placeholder:text-zinc-700 font-light animate-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Bouton de validation (Or Constant) */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full relative flex items-center justify-center gap-2 text-[10px] font-mono tracking-widest uppercase py-4 rounded-xl bg-gradient-to-r from-[#FFE082] via-[#D4AF37] to-[#B38728] text-black font-bold border border-transparent shadow-[0_4px_20px_rgba(212,175,55,0.15)] hover:shadow-[0_4px_25px_rgba(212,175,55,0.3)] hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none transition-all duration-300 cursor-pointer mt-8"
          >
            {isLoading ? (
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Se connecter</span>
                <ArrowRight size={12} className="stroke-[2.5]" />
              </>
            )}
          </button>
        </form>

        {/* Retour au site public */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors uppercase tracking-widest"
          >
            ← Retour au site public
          </a>
        </div>
      </motion.div>
    </main>
  );
}